import { useEffect, useState } from "react";
import { http } from "../lib/api";
import { InquiryModal } from "../components/InquiryModal";
import { Check, Zap, Crown, Rocket, ClipboardCheck, Armchair, Sparkles } from "lucide-react";
import { track } from "../lib/analytics";

const ICONS = {
  diagnostic: ClipboardCheck,
  blueprint: Sparkles,
  strategy: Zap,
  refresh: Rocket,
  overhaul: Crown,
  ops_chair: Armchair,
};

const ACCENTS = {
  diagnostic: "var(--accent-matrix)",
  blueprint: "var(--accent-amber)",
  strategy: "var(--accent-amber)",
  refresh: "var(--accent-flare)",
  overhaul: "var(--accent-flare)",
  ops_chair: "var(--accent-matrix)",
};

export const Business = () => {
  const [tiers, setTiers] = useState([]);
  const [activeTier, setActiveTier] = useState(null);

  useEffect(() => { http.get("/business/tiers").then((r) => setTiers(r.data)); }, []);

  return (
    <div data-testid="business-page" className="mx-auto max-w-7xl px-6 py-16">
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-3">// module.business</div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">
          business makeovers &amp; boosts
        </h1>
        <p className="text-neutral-400 mt-4 max-w-2xl">
          Six tiers, from a $199 async diagnostic to a $25,000 full-business overhaul. Every engagement starts with
          real diagnosis, not guesswork. Money-back guarantee on The Refresh.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {tiers.map((t) => {
          const Icon = ICONS[t.id] || Sparkles;
          const color = ACCENTS[t.id] || "var(--accent-amber)";
          const priceLabel = t.cadence === "monthly" ? `$${t.price_usd.toLocaleString()}/mo` : `$${t.price_usd.toLocaleString()}`;
          return (
            <div
              key={t.id}
              data-testid={`business-tier-${t.id}`}
              className="bg-[#0a0a0a] p-8 flex flex-col gap-4 relative"
            >
              {t.guarantee && (
                <div className="absolute top-4 right-4 text-[9px] uppercase tracking-widest text-[color:var(--accent-matrix)] border border-[color:var(--accent-matrix)]/40 px-2 py-0.5">
                  guaranteed
                </div>
              )}
              <Icon className="w-5 h-5" style={{ color }} />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{t.cadence === "monthly" ? "recurring" : "one-time"}</div>
                <div className="font-display font-bold text-xl mt-1">{t.name}</div>
                <div className="font-display font-bold text-3xl mt-2" style={{ color }}>{priceLabel}</div>
                <div className="text-xs text-neutral-500 mt-1">{t.timeline}</div>
              </div>
              <div className="text-sm text-neutral-300 leading-relaxed">{t.tagline}</div>
              <ul className="space-y-2 text-xs text-neutral-400 leading-relaxed">
                {t.deliverables.map((d, i) => (
                  <li key={`${t.id}-d-${i}`} className="flex gap-2">
                    <Check className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color }} />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
              {t.guarantee && (
                <div className="text-[10px] leading-relaxed text-[color:var(--accent-matrix)] border-t border-white/5 pt-3">
                  🛡 {t.guarantee}
                </div>
              )}
              <button
                data-testid={`business-inquire-${t.id}`}
                onClick={() => { track("inquiry_opened", { tier_id: t.id, tier_name: t.name, price_usd: t.price_usd }); setActiveTier(t); }}
                className="mt-auto inline-flex items-center justify-center gap-2 border px-4 py-3 text-[10px] uppercase tracking-widest font-bold duration-150"
                style={{
                  borderColor: color,
                  color,
                  transitionProperty: "background-color, color",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = color; e.currentTarget.style.color = "#000"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = color; }}
              >
                {t.cta}
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-16 border border-white/10 bg-[#0a0a0a] p-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-3">// how it works</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            ["01", "Inquire", "Fill the form for any tier. Takes 2 minutes."],
            ["02", "Fit call", "15-min free video call to confirm scope + timing."],
            ["03", "Invoice", "Stripe invoice for the tier. Pay via card, ACH, or wire."],
            ["04", "Deliver", "Work starts. You get regular updates. On the timeline promised."],
          ].map(([n, t, b]) => (
            <div key={n}>
              <div className="text-[10px] tracking-[0.3em] text-[color:var(--accent-amber)]">// {n}</div>
              <div className="font-display font-bold mt-2">{t}</div>
              <div className="text-xs text-neutral-400 mt-1 leading-relaxed">{b}</div>
            </div>
          ))}
        </div>
      </div>

      <InquiryModal
        tier={activeTier}
        open={!!activeTier}
        onClose={() => setActiveTier(null)}
      />
    </div>
  );
};
